import { RepairOrder } from "@/services/api";
import * as React from "react";
import format from "date-fns/format";
import { Checkmark20Regular } from "@fluentui/react-icons";
import { IColumnDefinition, PCSTable } from "../PCSTable";
import { Spinner } from "@fluentui/react-components";
import { CurrencyCell } from "../../atoms/CurrencyCell/CurrencyCell";
import styles from "./RepairOrdersTable.module.scss";
import { usePortalConfig } from "@/hooks/PortalConfig";
export interface IRequestTableProps {
	repairOrders: RepairOrder[];
	compact?: boolean;
	loading?: boolean;
	hideSubStatus?: boolean;
	emptyMessage?: string;
	enableShimmer?: boolean;
	shimmerRows?: number;
}

export function RepairOrdersTable(props: IRequestTableProps) {
	const pc = usePortalConfig();

	const columns: IColumnDefinition<RepairOrder>[] = React.useMemo<
		IColumnDefinition<RepairOrder>[]
	>(() => {
		const allCols: IColumnDefinition<RepairOrder>[] = [
			{
				columnId: "customerRO",
				headerText: "RO #",
				getValue: (i) => i.wipNumber,
				filterable: true,
				sortable: true,
				hidden: props.compact,
			},
			{
				columnId: "vehicle",
				headerText: "Vehicle",
				filterable: true,
				getValue: (i) => i.vehicle,
				sortable: true,
				wrap: true,
				hidden: props.compact,
			},
			{
				columnId: "vin",
				headerText: "VIN",
				sortable: true,
				getValue: (i) => i.vin,
				hidden: props.compact,
			},
			{
				columnId: "requestType",
				headerText: "Request Type",
				sortable: true,
				filterable: true,
				getValue: (i) => i.requestType,
				sizing: {
					minWidth: 200,
				},
			},

			{
				columnId: "estimator",
				headerText: "Estimator",
				sortable: true,
				filterable: true,
				getValue: (i) => i.estimator,
				hidden: true,
				compare(a, b) {
					const estA = a.estimator ?? "";
					const estB = b.estimator ?? "";
					return estA.localeCompare(estB);
				},
			},

			{
				columnId: "roTotal",
				headerText: "PCS RO Total $",
				hidden: true,
				sortable: true,
				getValue: (i) => i.roTotal,
				onRender(item) {
					return <CurrencyCell value={item.roTotal ?? 0} />;
				},
			},
			{
				columnId: "requestSubmitted",
				headerText: "PCS Submitted",
				sortable: true,
				hidden: false,
				getValue(item) {
					return item.requestSubmitted?.toISOString() ?? "";
				},
				onRender: (item: RepairOrder) =>
					!!item.requestSubmitted && format(item.requestSubmitted, "P"),
			},
			{
				columnId: "requestedDate",
				headerText: "Requested Date",
				sortable: true,
				getValue(item) {
					return item.requestedDate?.toISOString() ?? "";
				},
				onRender: (item: RepairOrder) => !!item.requestedDate && format(item.requestedDate, "P"),
			},
			{
				columnId: "requestScheduled",
				headerText: "PCS Assigned",
				hidden: !!pc.data?.preview,
				sortable: true,
				getValue(item) {
					return item.requestScheduled?.toISOString() ?? "";
				},
				onRender: (item: RepairOrder) =>
					!!item.requestScheduled && format(item.requestScheduled, "P"),
			},
			{
				columnId: "requestCompleted",
				headerText: "PCS Completed",
				sortable: true,
				hidden: !!pc.data?.preview,
				getValue(item) {
					return item.requestCompleted?.toISOString() ?? "";
				},
				onRender: (item: RepairOrder) =>
					!!item.requestCompleted && format(item.requestCompleted, "P"),
			},
			{
				columnId: "roNumber",
				headerText: "PCS RO #",
				hidden: !!pc.data?.preview,
				getValue: (item) => item.roNumber,
				sortable: true,
			},
			{
				columnId: "systemStatus",
				headerText: "PCS Status",
				getValue: (item) => item.systemStatus,
				sortable: true,
				filterable: true,
				hidden: !!pc.data?.preview,
			},
			{
				columnId: "substatus",
				headerText: "Reason",
				getValue: (item) => item.substatus,
				sortable: true,
				filterable: true,
				hidden: props.hideSubStatus || !!pc.data?.preview,
			},
		];

		return allCols;
	}, [props.compact]);

	return (
		<div className={styles.repairOrdersTable}>
			<PCSTable<RepairOrder>
				defaultSortState={{
					sortColumn: "requestSubmitted",
					sortDirection: "descending",
				}}
				searchable
				enableShimmer={props.loading || props.enableShimmer}
				shimmerRows={props.shimmerRows ?? 10}
				columns={columns}
				items={props.repairOrders}></PCSTable>

			{!props.loading && props.repairOrders.length === 0 && !!props.emptyMessage && (
				<div className={styles.empty}>
					<h3>{props.emptyMessage}</h3>
				</div>
			)}

			{props.loading && (
				<Spinner labelPosition="below" size="large" label={"Loading..."}>
					Loading
				</Spinner>
			)}
		</div>
	);
}
